import { useState, useEffect, useCallback } from "react";

import { makeStyles, Grid, Typography } from "@material-ui/core";
import _debounce from "lodash/debounce";

import { api } from "../../../../services";
import { COLORS } from "../../../../theme/colors";
import { ShaDowBox } from "../../../../components/boxes";
import { TradingRevenueCharges } from "./TradingRevenueCharges";
import { TextButton, Slider } from "../../../../components/base";

const useStyles = makeStyles((theme) => ({
  box: {
    padding: `${theme.spacing(3.5)}px ${theme.spacing(3)}px`,
  },
  title: {
    color: COLORS.darks[0][50],
    fontStyle: "italic",
  },
  row: {
    marginTop: theme.spacing(8),
    marginBottom: theme.spacing(5),
  },
  sliderTitle: {
    fontSize: 13,
  },
}));

export const UnrealizedTrading = () => {
  const classes = useStyles();
  const [data, setData] = useState();
  const [currentChartData, setCurrentChartData] = useState([]);
  const [estimationChartData, setEstimationChartData] = useState([]);
  const [currentAddingPowerRevenues, setCurrentAddingPowerRevenues] = useState();
  const [currentReducingUnrealizedRevenues, setCurrentReducingUnrealizedRevenues] = useState();
  const [estimationAddingPowerRevenues, setEstimationAddingPowerRevenues] = useState();
  const [estimationReducingUnrealizedRevenues, setEstimationReducingUnrealizedRevenues] =
    useState();

  useEffect(() => {
    const handleUnrealizedTrading = (url, queryParams) => {
      api.get(url).then((resp) => {
        const rawData = resp.data;
        if (!data) {
          setData({
            default_physical_vacancy_rate: rawData.physical_vacancy_rate_now,
            default_economic_vacancy_rate: rawData.economic_vacancy_rate_now,
            default_unconnected_tenants: rawData.unconnected_tenants_now,
          });
        }

        setCurrentAddingPowerRevenues(rawData.new_power_revenues);
        setCurrentReducingUnrealizedRevenues(
          rawData.revised_unrealized_unconnected_tenants +
            rawData.revised_unrealized_vacancy -
            (rawData.today_unrealized_unconnected_tenants + rawData.today_unrealized_vacancy)
        );
        setCurrentChartData([
          {
            unconnected_tenants: rawData.today_unrealized_unconnected_tenants,
            vacancy: rawData.today_unrealized_vacancy,
          },
          {
            unconnected_tenants: rawData.revised_unrealized_unconnected_tenants,
            vacancy: rawData.revised_unrealized_vacancy,
          },
          {
            new_power_revenues: rawData.new_power_revenues,
          },
        ]);
      });

      api.get(`${url}${queryParams ? "&" : "?"}years=20`).then((resp) => {
        const rawData = resp.data;
        setEstimationAddingPowerRevenues(rawData.new_power_revenues);
        setEstimationReducingUnrealizedRevenues(
          rawData.revised_unrealized_unconnected_tenants +
            rawData.revised_unrealized_vacancy -
            (rawData.today_unrealized_unconnected_tenants + rawData.today_unrealized_vacancy)
        );
        setEstimationChartData([
          {
            unconnected_tenants: rawData.today_unrealized_unconnected_tenants,
            vacancy: rawData.today_unrealized_vacancy,
          },
          {
            unconnected_tenants: rawData.revised_unrealized_unconnected_tenants,
            vacancy: rawData.revised_unrealized_vacancy,
          },
          {
            new_power_revenues: rawData.new_power_revenues,
          },
        ]);
      });
    };

    let url = `/praas/v1/unrealized/trading/`;
    let queryParams = [];
    if (data && "physical_vacancy_rate" in data) {
      let ph = data.physical_vacancy_rate;
      queryParams.push(`physical_vacancy=${ph}`);
    }
    if (data && "economic_vacancy_rate" in data) {
      let ev = data.economic_vacancy_rate;
      queryParams.push(`economic_vacancy=${ev}`);
    }
    if (data && "unconnected_tenants" in data) {
      let ut = data.unconnected_tenants;
      queryParams.push(`&unconnected_tenants=${ut}`);
    }

    if (queryParams) url += `?${queryParams.join("&")}`;

    handleUnrealizedTrading(url, queryParams);
  }, [data]);

  const debouncedChange = _debounce((key, value) => {
    setData((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, 400);

  const handleChange = (key, value) => {
    debouncedChange(key, value);
  };

  const handleClick = useCallback(() => {
    setData((prev) => ({
      ...prev,
      physical_vacancy_rate: data.default_physical_vacancy_rate,
      economic_vacancy_rate: data.default_economic_vacancy_rate,
      unconnected_tenants: data.default_unconnected_tenants,
    }));
  }, [data]);

  return (
    <>
      {data ? (
        <ShaDowBox style={{ marginBottom: 30 }}>
          <Grid container>
            <Grid
              className={classes.box}
              item
              xs={12}
              sm={4}
              style={{ borderRight: `1px solid ${COLORS.darks[2]}` }}
            >
              <Grid container justifyContent="space-between" alignItems="center">
                <Typography variant="h4">Unrealized Trading</Typography>
                <TextButton
                  text="Revert"
                  textStyle={{ fontWeight: 400, fontSize: 12 }}
                  onClick={handleClick}
                />
              </Grid>

              <Typography className={classes.title}>Drag parameters to change revenues</Typography>

              <Grid className={classes.row} container alignItems="center">
                <Grid item xs={5}>
                  <Typography className={classes.sliderTitle}>Physical vacancy rate</Typography>
                </Grid>
                <Grid item xs={7}>
                  <Slider
                    color="#E4DCD1"
                    value={data.physical_vacancy_rate || data.default_physical_vacancy_rate}
                    defaultValue={data.default_physical_vacancy_rate}
                    onChange={(e, newValue) => handleChange("physical_vacancy_rate", newValue)}
                  />
                </Grid>
              </Grid>
              <Grid className={classes.row} container alignItems="center">
                <Grid item xs={5}>
                  <Typography className={classes.sliderTitle}>Economic vacancy rate</Typography>
                </Grid>
                <Grid item xs={7}>
                  <Slider
                    color="#E4DCD1"
                    value={data.economic_vacancy_rate || data.default_economic_vacancy_rate}
                    defaultValue={data.default_economic_vacancy_rate}
                    onChange={(e, newValue) => handleChange("economic_vacancy_rate", newValue)}
                  />
                </Grid>
              </Grid>
              <Grid className={classes.row} container alignItems="center">
                <Grid item xs={5}>
                  <Typography className={classes.sliderTitle}>Unconnected tenants</Typography>
                </Grid>
                <Grid item xs={7}>
                  <Slider
                    color="#E1C9A7"
                    value={data.unconnected_tenants || data.default_unconnected_tenants}
                    defaultValue={data.default_unconnected_tenants}
                    onChange={(e, newValue) => handleChange("unconnected_tenants", newValue)}
                  />
                </Grid>
              </Grid>
            </Grid>

            <TradingRevenueCharges
              currentChartData={currentChartData}
              estimationChartData={estimationChartData}
              currentAddingPowerRevenues={currentAddingPowerRevenues}
              currentReducingUnrealizedRevenues={currentReducingUnrealizedRevenues}
              estimationAddingPowerRevenues={estimationAddingPowerRevenues}
              estimationReducingUnrealizedRevenues={estimationReducingUnrealizedRevenues}
            />
          </Grid>
        </ShaDowBox>
      ) : null}
    </>
  );
};
